import { Anchor, Box, Button, Group, Loader, LoadingOverlay, Stack, Table, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconLink, IconRefresh } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { TopicLikeFragment } from "src/gql/graphql";

type ArchivableTopic = Pick<TopicLikeFragment, "id" | "isCategory">;

export function useArchiveTopic({ id: topicId, isCategory }: ArchivableTopic, onArchive?: () => void) {
    const {
        data: {
            topic: { blockers },
        },
    } = useSuspenseQuery(gql.blockers(topicId));

    const { mutateAsync } = useGraphqlMutation(gql.archive);

    const Modal = () => {
        const {
            data: {
                topic: { blockers },
            },
            isFetching,
        } = useSuspenseQuery(gql.blockers(topicId));

        return (
            <Stack>
                <Text>Topic category can not be deleted until all sub-topics have been removed</Text>
                <Box pos="relative">
                    <LoadingOverlay visible={isFetching} />
                    <Table striped>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Topic</Table.Th>
                                <Table.Th>Description</Table.Th>
                                <Table.Th w={1}>
                                    <Button
                                        variant="default"
                                        rightSection={<IconRefresh />}
                                        onClick={() => {
                                            qk.invalidate("topic", "id", topicId).catch(notify.catch);
                                        }}
                                    >
                                        Refresh
                                    </Button>
                                </Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {blockers.topics.map((it) => (
                                <Table.Tr key={it.id}>
                                    <Table.Td>{it.name}</Table.Td>
                                    <Table.Td>
                                        <Text fz="sm">{it.description}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Anchor
                                            c="var(--mantine-color-text)"
                                            target="_blank"
                                            href={`/organization/topics/${it.id}`}
                                            aria-label="topic link"
                                        >
                                            <IconLink size={16} />
                                        </Anchor>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                    <Group w="100%" justify="flex-end" py="xs">
                        <Button
                            disabled={blockers.isBlocked}
                            onClick={() => void mutateAsync({ id: topicId }).then(onArchive).catch(notify.catch)}
                        >
                            Archive
                        </Button>
                    </Group>
                </Box>
            </Stack>
        );
    };

    async function archiveTopic() {
        if (blockers.isBlocked)
            modals.open({
                title: "Archive Category",
                children: (
                    <Suspense fallback={<Loader />}>
                        <Modal />
                    </Suspense>
                ),
                onClose: modals.closeAll,
                size: "lg",
            });
        else {
            await notify.confirm(`Are you sure you want to archive this ${isCategory ? "category" : "topic"}?`, {
                title: "Confirm",
            });
            mutateAsync({ id: topicId }).then(onArchive).catch(notify.catch);
            modals.closeAll();
        }
    }

    return {
        archiveTopic,
    };
}

const gql = {
    blockers: (topicId: string) =>
        queryOptions({
            queryKey: qk("topic", "id", topicId, "blockers"),
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query TopicBlockers($id: UUID!) {
                            topic(topicId: $id) {
                                blockers {
                                    isBlocked
                                    topics {
                                        ...TopicLike
                                    }
                                }
                            }
                        }
                    `),
                    { id: topicId },
                ),
        }),
    archive: graphql(`
        mutation ArchiveTopic($id: UUID!) {
            archiveTopic(id: $id) {
                blockers {
                    isBlocked
                    topics {
                        id
                        name
                    }
                }
            }
        }
    `),
};
